import React, { useState, useMemo } from 'react';
import { Network, AlertTriangle, FileText, Settings, Layers } from 'lucide-react';
import './KnowledgeGraphReasoning.css';

/**
 * Interactive Graph Visualization Component
 * Shows nodes in a flowing zigzag pattern with curved connections
 */
const KnowledgeGraphReasoning = ({ reasoningPath, isVisible }) => {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(0); // Default to first node
  const [showAllDetails, setShowAllDetails] = useState(true);

  // Calculate node positions with zigzag layout BEFORE early return
  const { nodes, edges } = useMemo(() => {
    if (!reasoningPath || reasoningPath.length === 0) {
      return { nodes: [], edges: [] };
    }

    const nodeCount = reasoningPath.length;
    const horizontalSpacing = 200; // Horizontal spacing between nodes
    const verticalAmplitude = 80; // How much nodes zigzag up/down
    const svgWidth = Math.max(700, nodeCount * horizontalSpacing);
    const svgHeight = 300; // Increased height for zigzag
    const centerY = svgHeight / 2;
    const startX = 80;

    // Create zigzag pattern: alternating top and bottom
    const calculatedNodes = reasoningPath.map((node, index) => {
      // Alternate between top (-amplitude) and bottom (+amplitude)
      const yOffset = index % 2 === 0 ? -verticalAmplitude : verticalAmplitude;

      return {
        ...node,
        x: startX + (index * horizontalSpacing),
        y: centerY + yOffset,
        id: `node-${index}`
      };
    });

    // Create curved edges with control points for smooth bezier curves
    const calculatedEdges = [];
    for (let i = 0; i < reasoningPath.length - 1; i++) {
      const from = calculatedNodes[i];
      const to = calculatedNodes[i + 1];

      // Calculate control points for smooth S-curve
      const midX = (from.x + to.x) / 2;
      const controlPoint1X = from.x + (to.x - from.x) * 0.3;
      const controlPoint2X = from.x + (to.x - from.x) * 0.7;

      calculatedEdges.push({
        id: `edge-${i}`,
        from: from,
        to: to,
        label: reasoningPath[i + 1].relationship || 'CONNECTED_TO',
        // Bezier curve path
        path: `M ${from.x} ${from.y} C ${controlPoint1X} ${from.y}, ${controlPoint2X} ${to.y}, ${to.x} ${to.y}`,
        // Label position (middle of curve)
        labelX: midX,
        labelY: (from.y + to.y) / 2
      });
    }

    return {
      nodes: calculatedNodes,
      edges: calculatedEdges,
      svgWidth,
      svgHeight
    };
  }, [reasoningPath]);

  // Early return AFTER hooks
  if (!isVisible || !reasoningPath || reasoningPath.length === 0) {
    return null;
  }

  const getNodeIcon = (nodeType) => {
    switch (nodeType) {
      case 'Vehicle':
        return '🚗';
      case 'System':
        return '🏗️';
      case 'Subsystem':
        return '🔧';
      case 'Component':
        return '⚙️';
      case 'Specification':
        return '📄';
      case 'Warning':
        return '⚠️';
      case 'Procedure':
        return '📋';
      case 'Guideline':
        return '💡';
      case 'Service':
        return '🔨';
      case 'Instruction':
        return '📖';
      default:
        return '🔷';
    }
  };

  const getNodeColor = (nodeType) => {
    switch (nodeType) {
      case 'Vehicle':
        return '#9b59b6'; // Purple
      case 'System':
        return '#3498db'; // Blue
      case 'Subsystem':
        return '#1abc9c'; // Teal
      case 'Component':
        return '#66B4C4';
      case 'Specification':
        return '#00ff88'; // Green
      case 'Warning':
        return '#ff6b6b'; // Red
      case 'Procedure':
        return '#ffd93d'; // Yellow
      case 'Guideline':
        return '#f39c12'; // Orange
      case 'Service':
        return '#e74c3c'; // Dark Red
      case 'Instruction':
        return '#16a085'; // Dark Teal
      default:
        return '#6c757d'; // Gray
    }
  };

  // Format node details with text excerpts (ENHANCED with new node types)
  const formatNodeDetails = (node) => {
    const data = node?.node_data || {};
    const details = [];

    // Debug logging
    console.log('Formatting node details:', {
      node_type: node?.node_type,
      node_data: data,
      has_data: Object.keys(data).length > 0
    });

    // Helper to truncate long text with ellipsis
    const truncate = (text, maxLength) => {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    if (node.node_type === 'Vehicle') {
      if (data.name) details.push({ label: 'Vehicle', value: data.name, type: 'title' });
      if (data.manufacturer) details.push({ label: 'Manufacturer', value: data.manufacturer, type: 'info' });
      if (data.model) details.push({ label: 'Model', value: data.model, type: 'info' });
      if (data.type) details.push({ label: 'Type', value: data.type, type: 'info' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.text) details.push({ label: 'Description', value: truncate(data.text, 200), type: 'excerpt' });
    } else if (node.node_type === 'System') {
      if (data.name) details.push({ label: 'System', value: data.name, type: 'title' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.description) details.push({ label: 'Description', value: truncate(data.description, 250), type: 'excerpt' });
    } else if (node.node_type === 'Subsystem') {
      if (data.name) details.push({ label: 'Subsystem', value: data.name, type: 'title' });
      if (data.parent_system) details.push({ label: 'Parent System', value: data.parent_system, type: 'info' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.text) details.push({ label: 'Context', value: truncate(data.text, 200), type: 'excerpt' });
    } else if (node.node_type === 'Component') {
      if (data.name) details.push({ label: 'Component', value: data.name, type: 'title' });
      if (data.system) details.push({ label: 'System', value: data.system, type: 'info' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      const pages = Array.isArray(data.page) ? data.page.join(', ') : data.page;
      if (pages) details.push({ label: 'Page', value: pages, type: 'page' });
      if (data.location) details.push({ label: 'Location', value: truncate(data.location, 150), type: 'info' });
      if (data.function) details.push({ label: 'Function', value: truncate(data.function, 150), type: 'info' });
      // Show context excerpt (why this page was retrieved) - prominently at the end
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.description) details.push({ label: 'Description', value: truncate(data.description, 200), type: 'excerpt' });
      else if (data.text) details.push({ label: 'Context', value: truncate(data.text, 200), type: 'excerpt' });
    } else if (node.node_type === 'Specification') {
      if (data.name) details.push({ label: 'Property', value: data.name, type: 'title' });
      if (data.value) details.push({ label: 'Value', value: `${data.value}${data.unit ? ' ' + data.unit : ''}`, type: 'value' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
    } else if (node.node_type === 'Warning') {
      if (data.severity) details.push({ label: 'Severity', value: data.severity, type: 'severity' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.text) details.push({ label: 'Warning Text', value: truncate(data.text, 300), type: 'excerpt' });
    } else if (node.node_type === 'Guideline') {
      if (data.category) details.push({ label: 'Category', value: data.category.toUpperCase(), type: 'info' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.text) details.push({ label: 'Guideline', value: truncate(data.text, 300), type: 'excerpt' });
    } else if (node.node_type === 'Service') {
      if (data.task) details.push({ label: 'Service Task', value: data.task, type: 'title' });
      if (data.interval_value && data.interval_unit) {
        details.push({ label: 'Interval', value: `Every ${data.interval_value} ${data.interval_unit}`, type: 'info' });
      }
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.text && data.text !== data.task) details.push({ label: 'Details', value: truncate(data.text, 200), type: 'excerpt' });
    } else if (node.node_type === 'Instruction') {
      if (data.operation) details.push({ label: 'Operation', value: data.operation, type: 'title' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.steps) details.push({ label: 'Instructions', value: truncate(data.steps, 300), type: 'excerpt' });
    } else if (node.node_type === 'Procedure') {
      if (data.name) details.push({ label: 'Procedure', value: data.name, type: 'title' });
      if (data.section) details.push({ label: 'Section', value: data.section, type: 'section' });
      if (data.page) details.push({ label: 'Page', value: data.page, type: 'page' });
      if (data.steps && data.steps.length > 0) {
        const stepsPreview = data.steps.slice(0, 3).join(' → ');
        const suffix = data.steps.length > 3 ? ` (+${data.steps.length - 3} more)` : '';
        details.push({ label: 'Steps', value: truncate(stepsPreview, 200) + suffix, type: 'info' });
      }
      // Show context excerpt (why this page was retrieved)
      if (data.context) details.push({ label: '📄 Page Excerpt', value: truncate(data.context, 300), type: 'excerpt' });
      else if (data.description) details.push({ label: 'Description', value: truncate(data.description, 250), type: 'excerpt' });
    }

    return details.length > 0 ? details : [{ label: 'Info', value: 'No details available', type: 'default' }];
  };

  const svgWidth = nodes.svgWidth || Math.max(700, nodes.length * 200);
  const svgHeight = nodes.svgHeight || 300;

  return (
    <div className="kg-reasoning-container">
      <div className="kg-reasoning-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Network size={18} />
          <h3>Knowledge Graph Reasoning Path</h3>
          <span className="kg-node-count">{nodes.length} steps</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => setSelectedNode(Math.max(0, selectedNode - 1))}
            disabled={selectedNode === 0}
            className="kg-nav-btn"
          >
            ← Prev
          </button>
          <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', minWidth: '60px', textAlign: 'center' }}>
            {selectedNode + 1} / {nodes.length}
          </span>
          <button
            onClick={() => setSelectedNode(Math.min(nodes.length - 1, selectedNode + 1))}
            disabled={selectedNode === nodes.length - 1}
            className="kg-nav-btn"
          >
            Next →
          </button>
        </div>
      </div>

      <div className="kg-main-content">
        {/* Left side: Graph visualization */}
        <div className="kg-graph-wrapper">
          <svg
            className="kg-graph-svg"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            preserveAspectRatio="xMidYMid meet"
          >
          {/* Define arrow marker for edges */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="8"
              refY="3"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3, 0 6"
                fill="rgba(102, 180, 196, 0.6)"
              />
            </marker>
          </defs>

          {/* Draw curved edges */}
          {edges.map((edge) => (
            <g key={edge.id} className="kg-edge-group">
              {/* Curved edge path */}
              <path
                d={edge.path}
                className="kg-edge-line"
                stroke={getNodeColor(edge.from.node_type)}
                strokeWidth="2.5"
                fill="none"
                opacity="0.5"
                markerEnd="url(#arrowhead)"
              />

              {/* Edge label with background */}
              <g>
                <rect
                  x={edge.labelX - 45}
                  y={edge.labelY - 12}
                  width="90"
                  height="24"
                  rx="12"
                  fill="rgba(10, 14, 39, 0.95)"
                  stroke={getNodeColor(edge.from.node_type)}
                  strokeWidth="1"
                  opacity="0.9"
                />
                <text
                  x={edge.labelX}
                  y={edge.labelY + 4}
                  className="kg-edge-label"
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.8)"
                  fontSize="10"
                  fontWeight="500"
                >
                  {edge.label.length > 15 ? edge.label.substring(0, 12) + '...' : edge.label}
                </text>
              </g>
            </g>
          ))}

          {/* Draw nodes */}
          {nodes.map((node, index) => (
            <g
              key={node.id}
              className="kg-node-group"
              onMouseEnter={() => setHoveredNode(index)}
              onMouseLeave={() => setHoveredNode(null)}
              onClick={() => setSelectedNode(index)}
              style={{ cursor: 'pointer' }}
            >
              {/* Selection ring */}
              {selectedNode === index && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="42"
                  fill="none"
                  stroke={getNodeColor(node.node_type)}
                  strokeWidth="3"
                  opacity="0.8"
                  className="kg-node-selected-ring"
                />
              )}

              {/* Node glow effect when hovered */}
              {(hoveredNode === index || selectedNode === index) && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="50"
                  fill={getNodeColor(node.node_type)}
                  opacity="0.2"
                  className="kg-node-glow"
                >
                  <animate
                    attributeName="r"
                    from="40"
                    to="55"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}

              {/* Node circle */}
              <circle
                cx={node.x}
                cy={node.y}
                r="35"
                fill="rgba(10, 14, 39, 0.95)"
                stroke={getNodeColor(node.node_type)}
                strokeWidth={hoveredNode === index ? "3.5" : "2.5"}
                className="kg-node-circle"
                style={{
                  transition: 'all 0.3s ease',
                  filter: 'none'
                }}
              />

              {/* Node type icon (emoji) */}
              <text
                x={node.x}
                y={node.y + 5}
                textAnchor="middle"
                fontSize="20"
                style={{ pointerEvents: 'none' }}
              >
                {getNodeIcon(node.node_type)}
              </text>

              {/* Node type label below */}
              <text
                x={node.x}
                y={node.y + 60}
                textAnchor="middle"
                fill={getNodeColor(node.node_type)}
                fontSize="13"
                fontWeight="600"
                style={{ pointerEvents: 'none' }}
              >
                {node.node_type}
              </text>

              {/* Step number badge */}
              <g>
                <circle
                  cx={node.x}
                  cy={node.y - 50}
                  r="15"
                  fill={getNodeColor(node.node_type)}
                  opacity="0.3"
                />
                <text
                  x={node.x}
                  y={node.y - 46}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.9)"
                  fontSize="12"
                  fontWeight="bold"
                  style={{ pointerEvents: 'none' }}
                >
                  {node.step}
                </text>
              </g>
            </g>
          ))}
        </svg>
        </div>

        {/* Right side: Details Panel - Always visible */}
        <div className="kg-details-sidebar">
          {nodes[selectedNode] ? (
            <>
              <div className="kg-details-sidebar-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{getNodeIcon(nodes[selectedNode]?.node_type)}</span>
                  <div>
                    <div style={{ color: getNodeColor(nodes[selectedNode]?.node_type), fontWeight: '600', fontSize: '1rem' }}>
                      {nodes[selectedNode]?.node_type}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                      Step {nodes[selectedNode]?.step} of {nodes.length}
                    </div>
                  </div>
                </div>
              </div>

              <div className="kg-details-sidebar-content">
                {formatNodeDetails(nodes[selectedNode]).length > 0 ? (
                  formatNodeDetails(nodes[selectedNode]).map((detail, i) => (
                    <div key={i} className={`kg-detail-item kg-detail-${detail.type}`}>
                      <div className="kg-detail-label">{detail.label}</div>
                      <div className="kg-detail-value">{detail.value}</div>
                    </div>
                  ))
                ) : (
                  <div className="kg-detail-item">
                    <div className="kg-detail-label">Info</div>
                    <div className="kg-detail-value">No details available for this node</div>
                  </div>
                )}
              </div>

              {nodes[selectedNode]?.relationship && selectedNode < nodes.length - 1 && (
                <div className="kg-connection-info">
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>
                    CONNECTED TO NEXT NODE VIA
                  </div>
                  <div style={{ color: getNodeColor(nodes[selectedNode]?.node_type), fontWeight: '600', fontSize: '0.9rem' }}>
                    {nodes[selectedNode + 1]?.relationship}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="kg-detail-item">
              <div className="kg-detail-label">Info</div>
              <div className="kg-detail-value">Select a node to view details</div>
            </div>
          )}
        </div>
      </div>

      <div className="kg-reasoning-footer">
        <div className="kg-legend">
          <span className="kg-legend-label">💡 Click nodes to view details • Use Prev/Next to navigate</span>
          <div className="kg-legend-items">
            <span className="kg-legend-item">
              <span style={{ color: '#9b59b6' }}>●</span> Vehicle
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#3498db' }}>●</span> System
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#1abc9c' }}>●</span> Subsystem
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#66B4C4' }}>●</span> Component
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#00ff88' }}>●</span> Spec
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#ff6b6b' }}>●</span> Warning
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#f39c12' }}>●</span> Guideline
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#e74c3c' }}>●</span> Service
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#16a085' }}>●</span> Instruction
            </span>
            <span className="kg-legend-item">
              <span style={{ color: '#ffd93d' }}>●</span> Procedure
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphReasoning;
